import React from 'react';
import { FONT_MONO } from '../landing/landingData';
import { Icons } from '../../components/studio/studioUi';
import { PLATFORM_LABEL, POST_STATUS_LABEL, type PostMetrics, type ScheduledPost, type SocialAccount, type SocialPlatform } from '../../services/social.service';

/** Plain colour chips (no logos): enough to tell the platforms apart in a list. */
const PLATFORM_COLOR: Record<SocialPlatform, string> = {
    youtube: '#D23B2C',
    tiktok: '#1F1F1F',
    linkedin: '#2867B2',
    facebook: '#3B5BA9',
    instagram: '#B8487A',
    threads: '#101010',
};

export const PlatformChip: React.FC<{ platform: SocialPlatform }> = ({ platform }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--hv-body)' }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: PLATFORM_COLOR[platform] }} />
        {PLATFORM_LABEL[platform]}
    </span>
);

export const AccountAvatar: React.FC<{ account: SocialAccount; size?: number }> = ({ account, size = 30 }) => (
    <span style={{ width: size, height: size, flex: 'none', borderRadius: 99, overflow: 'hidden', display: 'grid', placeItems: 'center', background: PLATFORM_COLOR[account.platform], color: '#fff', fontSize: size * 0.42, fontWeight: 700 }}>
        {account.avatarUrl ? <img src={account.avatarUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : account.name.replace(/^@/, '').slice(0, 1).toUpperCase()}
    </span>
);

const STATUS_COLOR: Record<ScheduledPost['status'], string> = {
    SCHEDULED: 'var(--hv-ink)',
    PUBLISHING: 'var(--hv-leaf)',
    PUBLISHED: 'var(--hv-leaf)',
    FAILED: 'var(--hv-danger)',
    CANCELED: 'var(--hv-muted)',
};

const compact = new Intl.NumberFormat('id-ID', { notation: 'compact', maximumFractionDigits: 1 });
const METRIC_LABEL: [keyof PostMetrics, string][] = [['views', 'tayangan'], ['likes', 'suka'], ['comments', 'komentar'], ['shares', 'dibagikan'], ['saves', 'disimpan']];

/** "1,2 rb tayangan · 34 suka · …" with when it was read, or why it could not be. */
const PostStats: React.FC<{ post: ScheduledPost }> = ({ post }) => {
    const parts = METRIC_LABEL.filter(([k]) => post.metrics?.[k] !== undefined).map(([k, label]) => `${compact.format(post.metrics![k]!)} ${label}`);
    if (!parts.length && !post.metricsError) return null;
    return (
        <div style={{ display: 'grid', gap: 2 }}>
            {parts.length > 0 && <div style={{ fontWeight: 600, color: 'var(--hv-ink)' }}>{parts.join(' · ')}</div>}
            {post.metricsError ? (
                <div style={{ color: 'var(--hv-muted)', fontSize: 11.5 }}>{post.metricsError}</div>
            ) : (
                post.metricsAt && <div style={{ color: 'var(--hv-muted)', fontSize: 11.5 }}>Statistik per {fmtWhen(post.metricsAt)}</div>
            )}
        </div>
    );
};

export const fmtWhen = (iso: string) => new Date(iso).toLocaleString('id-ID', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** One scheduled / sent post with its actions. */
export const PostRow: React.FC<{ post: ScheduledPost; showVideo?: boolean; onCancel: () => void; onRetry: () => void; onReschedule?: () => void }> = ({ post, showVideo, onCancel, onRetry, onReschedule }) => (
    <div style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', fontSize: 12.5, display: 'grid', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <AccountAvatar account={post.account} size={26} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{post.account.name}</div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <PlatformChip platform={post.platform} />
                    {post.options?.placement === 'story' && <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.12em', color: 'var(--hv-muted)' }}>· STORY</span>}
                </span>
            </div>
            <b style={{ color: STATUS_COLOR[post.status] }}>{POST_STATUS_LABEL[post.status]}</b>
        </div>
        {showVideo && <div style={{ color: 'var(--hv-body)' }}>{post.videoName}</div>}
        <div style={{ color: 'var(--hv-muted)' }}>{post.status === 'PUBLISHED' && post.publishedAt ? `Tayang ${fmtWhen(post.publishedAt)}` : fmtWhen(post.scheduledAt)}</div>
        {post.caption && <div style={{ color: 'var(--hv-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={post.caption}>{post.caption}</div>}
        {post.status === 'PUBLISHED' && <PostStats post={post} />}
        {post.status === 'FAILED' && post.error && <div style={{ color: 'var(--hv-danger)', overflowWrap: 'anywhere' }}>{post.error}</div>}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {post.externalUrl && <a href={post.externalUrl} target="_blank" rel="noreferrer" style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icons.external size={12} /> Lihat post</a>}
            {post.status === 'SCHEDULED' && onReschedule && <LinkBtn onClick={onReschedule}>Ubah waktu</LinkBtn>}
            {post.status === 'FAILED' && <LinkBtn onClick={onRetry}>Coba lagi</LinkBtn>}
            {post.status === 'SCHEDULED' && <LinkBtn danger onClick={onCancel}>Batalkan</LinkBtn>}
            {(post.status === 'FAILED' || post.status === 'CANCELED' || post.status === 'PUBLISHED') && <LinkBtn onClick={onCancel}>Hapus dari daftar</LinkBtn>}
        </div>
    </div>
);

const LinkBtn: React.FC<{ onClick: () => void; danger?: boolean; children: React.ReactNode }> = ({ onClick, danger, children }) => (
    <button type="button" onClick={onClick} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, color: danger ? 'var(--hv-danger)' : 'var(--hv-ink)' }}>{children}</button>
);

/** `datetime-local` value (local time) ↔ ISO. */
export function toLocalInput(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const TZ_LABEL = (() => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return '';
    }
})();

/**
 * YouTube API Services Developer Policies: users agree to the YouTube Terms of
 * Service and to our privacy policy (which links Google's), and learn they can
 * revoke access in Google's security settings. Shown where they connect and post.
 */
export const YoutubeConsent: React.FC<{ connecting?: boolean }> = ({ connecting }) => {
    const link = (href: string, text: string) => <a href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--hv-ink)', fontWeight: 600 }}>{text}</a>;
    return (
        <div style={{ fontSize: 11.5, lineHeight: 1.55, color: 'var(--hv-muted)' }}>
            HyVay memakai YouTube API Services. {connecting ? 'Dengan menghubungkan' : 'Dengan memposting ke'} YouTube, kamu menyetujui {link('https://www.youtube.com/t/terms', 'Persyaratan Layanan YouTube')} serta {link('/privacy', 'Kebijakan Privasi HyVay')} dan {link('https://policies.google.com/privacy', 'Kebijakan Privasi Google')}. Akses bisa kamu cabut kapan saja lewat tombol Putuskan di HyVay atau di {link('https://security.google.com/settings/security/permissions', 'pengaturan keamanan Google')}.
        </div>
    );
};
